import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { X, Globe2, Loader2, Play, Pause, History } from "lucide-react";
import { api } from "../../lib/api";
import { mediaSrc } from "../site/HeroSlides";

const icon = (live) => L.divIcon({ className: "", html: `<div class="wm-pin${live ? " live" : ""}"></div>`, iconSize: [18, 18], iconAnchor: [9, 9] });
const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

function FitPins({ pins }) {
  const map = useMap();
  useEffect(() => {
    if (!pins.length) return;
    if (pins.length === 1) map.flyTo([pins[0].lat, pins[0].lng], 4, { duration: 0.8 });
    else map.flyToBounds(pins.map((p) => [p.lat, p.lng]), { padding: [60, 60], maxZoom: 5, duration: 0.8 });
  }, [pins, map]);
  return null;
}

function Replay({ weeks, thisWeek, idx, setIdx }) {
  const [playing, setPlaying] = useState(false);
  const t = useRef(null);
  useEffect(() => {
    if (!playing) return undefined;
    t.current = setInterval(() => setIdx((i) => { if (i + 1 >= weeks.length) { setPlaying(false); return i; } return i + 1; }), 1600);
    return () => clearInterval(t.current);
  }, [playing, weeks.length, setIdx]);
  const w = idx >= 0 ? weeks[idx] : null;
  const todayIdx = Math.max(0, weeks.findIndex((x) => x.week === thisWeek));
  const label = !w ? "Today" : `${w.week === thisWeek ? "This week" : w.week > thisWeek ? "Coming up" : "Week of"} · ${fmt(w.week)}`;
  return (
    <div className="absolute bottom-4 inset-x-4 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-[560px] z-[500] bg-black/75 backdrop-blur-md border border-white/10 rounded-2xl px-4 py-3" data-testid="world-map-replay">
      <div className="flex items-center gap-3 mb-2">
        <button onClick={() => { if (idx < 0 || idx >= weeks.length - 1) setIdx(0); setPlaying(!playing); }} className="w-9 h-9 rounded-full bg-[var(--gold)] text-black flex items-center justify-center shrink-0" aria-label={playing ? "Pause replay" : "Play replay"} data-testid="world-map-play">{playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}</button>
        <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate" data-testid="world-map-week-label">{label}</p><p className="text-[11px] text-white/60">{w ? `${w.count} wedding${w.count === 1 ? "" : "s"} shared` : "Married around the world right now"}</p></div>
        <button onClick={() => { setPlaying(false); setIdx(-1); }} className={`text-xs rounded-full px-3 py-1 transition-colors ${idx < 0 ? "bg-white text-black" : "bg-white/10 hover:bg-white/20"}`} data-testid="world-map-today">Today</button>
      </div>
      <input type="range" min={0} max={Math.max(0, weeks.length - 1)} value={idx >= 0 ? idx : todayIdx} onChange={(e) => { setPlaying(false); setIdx(Number(e.target.value)); }} className="w-full accent-[var(--gold)]" aria-label="Pick a week" data-testid="world-map-slider" />
      <div className="flex justify-between text-[10px] text-white/50"><span>{fmt(weeks[0].week)}</span><span className="flex items-center gap-1"><History className="w-3 h-3" /> All time, by week</span><span>{fmt(weeks[weeks.length - 1].week)}</span></div>
    </div>
  );
}

export function WorldWeddingMap({ onClose, onOpen }) {
  const [pins, setPins] = useState(null);
  const [weeks, setWeeks] = useState(null);
  const [idx, setIdx] = useState(-1);
  useEffect(() => { api.get("/stories/world-map/weeks").then((r) => setWeeks(r.data)).catch(() => setWeeks({ weeks: [] })); }, []);
  const week = idx >= 0 && weeks ? weeks.weeks[idx]?.week : "";
  useEffect(() => { api.get("/stories/world-map", { params: week ? { week } : {} }).then((r) => setPins(r.data)).catch(() => setPins([])); }, [week]);
  return (
    <div className="fixed inset-0 z-[70] bg-black text-white flex flex-col" data-testid="world-map">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <p className="flex items-center gap-2 font-serif text-xl"><Globe2 className="w-5 h-5 text-[var(--gold)]" /> {week ? "Map replay" : "Married around the world today"}</p>
        <button onClick={onClose} aria-label="Close map" data-testid="world-map-close"><X className="w-6 h-6" /></button>
      </div>
      <div className="relative flex-1">
        {pins === null ? <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <MapContainer center={[20, 10]} zoom={2} minZoom={2} worldCopyJump maxBounds={[[-85, -400], [85, 400]]} maxBoundsViscosity={1} className="absolute inset-0" style={{ background: "#262626" }}>
            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}" attribution="Tiles &copy; Esri" maxZoom={16} />
            <FitPins pins={pins} />
            {pins.map((p) => (
              <Marker key={p.wedding_id} position={[p.lat, p.lng]} icon={icon(p.live)}>
                <Popup>
                  <div className="w-44 text-[var(--ink)]" data-testid={`world-pin-${p.slug}`}>
                    {p.cover_image && <img src={mediaSrc(p.cover_image)} alt="" className="w-full h-24 object-cover rounded mb-2" />}
                    <p className="font-serif text-base leading-tight">{p.couple_names}</p>
                    <p className="text-[11px] text-[var(--muted)] mb-2">{p.city}{p.wedding_date ? ` · ${fmt(p.wedding_date)}` : ""}{p.live ? " · LIVE now" : ""}</p>
                    <div className="flex gap-2 text-xs">
                      {p.count > 0 && <button onClick={() => onOpen(p)} className="underline text-[var(--gold)]" data-testid={`world-pin-stories-${p.slug}`}>Stories</button>}
                      <Link to={`/site/${p.slug}/social`} className="underline !text-[var(--gold)]" data-testid={`world-pin-feed-${p.slug}`}>Their feed</Link>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
        {pins?.length === 0 && <p className={`absolute ${weeks?.weeks?.length ? "bottom-36" : "bottom-6"} inset-x-0 text-center text-sm text-white/60 z-[500]`} data-testid="world-map-empty">{week ? "No weddings shared on the map that week." : "No weddings are sharing on the map today yet."}</p>}
        {weeks?.weeks?.length > 0 && <Replay weeks={weeks.weeks} thisWeek={weeks.this_week} idx={idx} setIdx={setIdx} />}
      </div>
    </div>
  );
}
